import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS } from "../../../mocks/siteData";

export interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

export interface ProcessContent {
  eyebrow: string;
  headline: string;
  steps: ProcessStep[];
}

interface Props {
  content: ProcessContent;
}

const stepImages = [
  "https://public.readdy.ai/ai/img_res/23c5bb15f00d0141c1a4948fe79268d7.jpg",
  "https://readdy.ai/api/search-image?query=close-up%20of%20lash%20artist%20using%20precision%20tweezers%20to%20map%20out%20eyelash%20extension%20design%20on%20client%2C%20detailed%20artistic%20planning%20shot%2C%20luxury%20beauty%20studio%20white%20background%2C%20professional%20editorial%20beauty%20photography%2C%20artisan%20craftsmanship%20detail&width=480&height=340&seq=process-step-2-design&orientation=landscape",
  "https://readdy.ai/api/search-image?query=extreme%20macro%20close-up%20of%20gloved%20professional%20hands%20applying%20individual%20eyelash%20extensions%20with%20precision%20tweezers%2C%20perfect%20detailed%20artisan%20work%2C%20beauty%20studio%20white%20lighting%2C%20ultra%20sharp%20focus%2C%20editorial%20beauty%20photography%2C%20luxury%20precision&width=480&height=340&seq=process-step-3-application&orientation=landscape",
  "https://readdy.ai/api/search-image?query=beautiful%20confident%20woman%20admiring%20her%20new%20perfect%20eyelash%20extensions%20in%20mirror%2C%20luxury%20beauty%20salon%2C%20warm%20flattering%20lighting%2C%20radiant%20happy%20expression%2C%20elegant%20salon%20environment%2C%20editorial%20beauty%20photography%2C%20stunning%20transformation%20result&width=480&height=340&seq=process-step-4-aftercare&orientation=landscape",
];

export default function ProcessSection({ content }: Props) {
  const { eyebrow, headline, steps } = content;

  return (
    <section id="process" className="relative bg-[#0D0D0D] py-24 md:py-32 overflow-hidden">
      {/* Background decorative element */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none select-none overflow-hidden flex items-center justify-end">
        <span className="font-serif text-[22vw] font-light text-white leading-none pr-8">04</span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <RevealWrapper>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 md:mb-20">
            <div>
              <div className="flex items-center gap-4 mb-5">
                <span className="block w-10 h-px bg-gold/50" />
                <span className="section-eyebrow text-gold/90 tracking-[0.25em]">{eyebrow}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight">
                {headline}
              </h2>
            </div>
            <p className="font-sans text-sm text-white/35 max-w-xs font-light leading-relaxed lg:text-right">
              Every appointment follows our signature four-step process — ensuring a personalized, premium experience from start to finish.
            </p>
          </div>
        </RevealWrapper>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <RevealWrapper key={i} delay={i * 0.12}>
              <article className="group relative bg-[#161616] border border-white/6 hover:border-gold/20 transition-all duration-500 overflow-hidden">
                {/* Step image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={stepImages[i]}
                    alt={`Step ${step.number}: ${step.title}`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161616] via-[#161616]/20 to-transparent" />
                  {/* Step number overlay */}
                  <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center border border-gold/40 bg-black/50 backdrop-blur-sm">
                    <span className="font-serif text-gold text-sm font-light">{step.number}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  {/* Icon */}
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/10 border border-gold/20 text-gold mb-5 group-hover:bg-gold/20 transition-colors duration-300">
                    <i className={`${step.icon} text-sm`} />
                  </div>
                  <h3 className="font-serif text-xl font-light text-white mb-3 group-hover:text-gold transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="font-sans text-xs text-white/45 leading-relaxed font-light">
                    {step.description}
                  </p>
                </div>

                {/* Bottom gold accent line on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </article>
            </RevealWrapper>
          ))}
        </div>

        {/* Connecting timeline indicator (desktop) */}
        <div className="hidden xl:flex items-center justify-between mt-0 -mt-[1px] px-0 pointer-events-none select-none">
          {steps.map((_, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className="flex-1 h-px bg-gradient-to-r from-gold/20 to-gold/10" />
              {i === steps.length - 1 && <div className="w-1.5 h-1.5 bg-gold/40 rounded-full" />}
            </div>
          ))}
        </div>

        {/* Mid-page CTA */}
        <RevealWrapper delay={0.55}>
          <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-white/6">
            <p className="font-serif text-xl text-white/40 font-light italic">
              &ldquo;Ready to begin your transformation?&rdquo;
            </p>
            <a
              href={BUSINESS.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold whitespace-nowrap"
            >
              Book Your Lash Consultation
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </RevealWrapper>
      </div>
    </section>
  );
}
